import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { 
  User, Phone, EnvelopeSimple, DotsThree, FileText, Info
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

import { API } from '../config/api';
import { formatEuro } from '../lib/whatsapp';

const QUOTE_STATUS = { draft: 'bozza', sent: 'inviato', accepted: 'accettato', rejected: 'rifiutato', won: 'vinto', lost: 'perso' };

const pipelineStagesDef = [
  { id: 'new_lead', color: 'border-t-blue-500' },
  { id: 'contacted', color: 'border-t-purple-500' },
  { id: 'site_visit', color: 'border-t-yellow-500' },
  { id: 'quote_sent', color: 'border-t-orange-500' },
  { id: 'negotiation', color: 'border-t-pink-500' },
  { id: 'won', color: 'border-t-green-500' },
  { id: 'lost', color: 'border-t-red-500' }
];

export default function PipelinePage() {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [stageTotals, setStageTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const pipelineStages = pipelineStagesDef.map(s => ({ ...s, label: t(`pipeline.columns.${s.id}`) }));

  const fetchClients = async () => {
    try {
      // The board with each client's quotes; stages follow the quotes (sent, accepted, rejected)
      const { data } = await axios.get(`${API}/pipeline`);
      setClients(Array.isArray(data?.clients) ? data.clients : []);
      setStageTotals(data?.stage_totals || {});
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const moveClient = async (clientId, newStage) => {
    try {
      await axios.put(`${API}/clients/${clientId}`, { pipeline_stage: newStage });
      toast.success(t('pipeline.clientMoved'));
      fetchClients();
    } catch (err) {
      console.error('Error moving client:', err);
      toast.error(t('pipeline.moveError'));
    }
  };

  const getClientsForStage = (stageId) => {
    return (clients || []).filter(c => c.pipeline_stage === stageId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      {/* Header */}
      <div>
        <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A]">
          {t('pipeline.salesPipeline')}
        </h1>
        <p className="text-[#4A5568] mt-1">
          {t('pipeline.manageProcess')}
        </p>
        <p className="text-xs text-[#4A5568] mt-2 flex items-start gap-1.5" data-testid="pipeline-auto-info">
          <Info size={14} className="mt-0.5 shrink-0 text-[#0033A0]" />
          I clienti si spostano da soli con i preventivi: in preparazione → Sopralluogo, inviato → Preventivo inviato,
          accettato → Vinto, rifiutato → Perso. Puoi sempre spostarli a mano dal menu ⋯.
        </p>
      </div>

      {/* Pipeline Board */}
      {/* Phones/tablets: scroll sideways. Desktop: every stage side by side on one screen. */}
      <div className="overflow-x-auto pb-4 lg:overflow-visible">
        <div className="flex gap-3 min-w-max lg:min-w-0 lg:grid lg:grid-cols-7 lg:gap-2" data-testid="pipeline-board">
          {pipelineStages.map((stage) => (
            <div
              key={stage.id}
              className="w-64 flex-shrink-0 lg:w-auto lg:min-w-0"
              data-testid={`pipeline-stage-${stage.id}`}
            >
              <div className={`bg-white border border-[#E2E8F0] rounded-sm border-t-4 ${stage.color}`}>
                {/* Stage Header */}
                <div className="px-4 py-3 lg:px-2 lg:py-2 lg:min-h-[4.75rem] border-b border-[#E2E8F0] flex items-center justify-between">
                  <div className="min-w-0">
                    <h3 className="font-medium text-[#0A0F1A] lg:text-sm leading-tight" title={stage.label}>{stage.label}</h3>
                    <p className="text-xs text-[#4A5568] lg:text-[11px]">
                      {getClientsForStage(stage.id).length} {t('pipeline.clients')}
                      {stageTotals[stage.id] > 0 && (
                        <span className="font-semibold text-[#0A0F1A] lg:block" data-testid={`pipeline-total-${stage.id}`}><span className="lg:hidden"> · </span>{formatEuro(stageTotals[stage.id])}</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Stage Content */}
                <div className="p-2 lg:p-1.5 min-h-[400px] lg:min-h-[300px] lg:max-h-[calc(100vh-240px)] lg:overflow-y-auto space-y-2">
                  {getClientsForStage(stage.id).map((client) => (
                    <ClientCard
                      key={client.id}
                      client={client}
                      onMove={moveClient}
                      currentStage={stage.id}
                      pipelineStages={pipelineStages}
                      t={t}
                      navigate={navigate}
                    />
                  ))}
                  {getClientsForStage(stage.id).length === 0 && (
                    <div className="text-center py-8 text-sm lg:text-xs text-[#4A5568]">
                      {t('pipeline.noClient')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ClientCard({ client, onMove, currentStage, pipelineStages, t, navigate }) {
  const summary = client.quotes_summary || {};
  const latest = summary.latest;
  return (
    <div
      className="bg-white border border-[#E2E8F0] rounded-sm p-3 lg:p-2 hover:border-[#0033A0] transition-colors cursor-pointer"
      title={[client.name, client.email, client.phone].filter(Boolean).join(' · ')}
      onClick={() => navigate(`/clients/${client.id}`)}
      data-testid={`client-card-${client.id}`}
    >
      <div className="flex items-start justify-between gap-1 mb-2 lg:mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-[#0033A0]/10 flex items-center justify-center shrink-0 lg:hidden">
            <User size={16} className="text-[#0033A0]" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm lg:text-xs text-[#0A0F1A] truncate lg:whitespace-normal lg:break-words lg:line-clamp-2">
              {client.name}
            </p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={(e) => e.stopPropagation()}>
              <DotsThree size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {pipelineStages
              .filter(s => s.id !== currentStage)
              .map(stage => (
                <DropdownMenuItem
                  key={stage.id}
                  onClick={() => onMove(client.id, stage.id)}
                >
                  {t('pipeline.moveTo')} {stage.label}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <div className="space-y-1 text-xs text-[#4A5568] lg:hidden">
        {client.email && (
          <div className="flex items-center gap-1 truncate">
            <EnvelopeSimple size={12} />
            <span className="truncate">{client.email}</span>
          </div>
        )}
        {client.phone && (
          <div className="flex items-center gap-1">
            <Phone size={12} />
            <span>{client.phone}</span>
          </div>
        )}
      </div>
      {latest && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate(`/quotes/${latest.id}`); }}
          className="mt-2 lg:mt-1 w-full text-left flex flex-wrap items-center justify-between gap-x-2 rounded-sm bg-[#F4F6F8] hover:bg-[#E2E8F0] px-2 py-1.5 lg:px-1.5 lg:py-1 text-xs lg:text-[11px]"
          data-testid={`client-card-quote-${client.id}`}
        >
          <span className="flex items-center gap-1 text-[#0A0F1A] truncate min-w-0">
            <FileText size={12} className="text-[#0033A0] shrink-0" />
            {latest.quote_number} · {QUOTE_STATUS[latest.status] || latest.status}
          </span>
          <span className="font-semibold text-[#0A0F1A] shrink-0">{formatEuro(latest.total)}</span>
        </button>
      )}
      {summary.count > 1 && (
        <p className="mt-1 text-[11px] lg:text-[10px] text-[#4A5568]">{summary.count} preventivi · aperti {formatEuro(summary.open_total)}</p>
      )}
    </div>
  );
}

